import { merge, noop } from "lodash";
import { FC, useEffect, useMemo, useState } from "react";
import { DeepPartial } from "react-hook-form";
import { Tabs } from "../../../../components/tabs/tabs-page";
import { VerificationDTO } from "../../../../store/verification/models/dto/due-diligence";
import { VerificationService } from "../../../../store/verification/service";
import { deepOmitBy } from "../../../../utils/utils";
import { DueDiligenceConfigPage } from "../../common/config-page";
import FormEntries from "./entries";
import RiskAssessment from "./risk-assessment";
import RiskScoring from "./risk-scoring";


type FormType = VerificationDTO.Requests.DueDiligence.Config.Post.T;

export const useLoadDefaults = (
  item?: VerificationDTO.Responses.DueDiligence.Config.Get.T
) => {
  const [loaded, setLoaded] = useState(false);
  const [defaultImage, setDefaultImage] = useState<File>();

  useEffect(() => {
    if (item) {
      VerificationService.getDueDiligenceHeaderImage(item.id)
        .then(async (res) => {
          const blob = await res.blob();
          setDefaultImage(
            new File([blob], "default", {
              type: blob.type || undefined,
            })
          );
        })
        .catch(noop)
        .finally(() => {
          setLoaded(true);
        });
    }
  }, [item]);

  return {
    defaults: useMemo<DeepPartial<FormType>>(
      () =>
        merge(
          {},
          VerificationDTO.Requests.DueDiligence.Config.Post.defaults,
          deepOmitBy(item, (x) => x === null || x === undefined),
          item && item.expiresAt ? { expiresAt: item.expiresAt.toDate() } : {}
        ),
      [loaded, item]
    ),
    image: defaultImage,
  };
};

export const SingleDueDiligenceConfigPage: FC<{
  item: VerificationDTO.Responses.DueDiligence.Config.Get.T;
}> = ({ item }) => {
  const { defaults, image } = useLoadDefaults(item);

  const [
    data,
    setData,
  ] = useState<VerificationDTO.Responses.DueDiligence.Form.Get.T>();

  useEffect(() => {
    if (item.dueDiligenceId) {
      VerificationService.getSingleDueDiligenceForm(item.dueDiligenceId)
        .then((d) => setData(d))
        .catch(noop);
    }
  }, [item.dueDiligenceId]);

  const tabs = useMemo(
    () => [
      ...(data
        ? [
          {
            title: "Entries",
            content: <FormEntries data={data} configId={item.id} />,
          },
        ]
        : []),
      {
        title: "Risk Assessment",
        content: <RiskAssessment duediligenceId={item.id} />
      },
      {
        title: "Risk Scoring",
        content: <RiskScoring duediligenceId={item.id} />
      },
      {
        title: "Configuration",
        content: (
          <DueDiligenceConfigPage
            defaultValues={defaults}
            image={image}
            onSubmit={(data) =>
              VerificationService.updateDueDiligenceConfig(item.id, data)
            }
            enabledOptionalFields={item.enabledOptionalFields}
            url={item.url}
          >
            Update Config
          </DueDiligenceConfigPage>
        ),
      },
    ],
    [defaults, image, data]
  );

  return <Tabs noMargin tabs={tabs} />;
};
